import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { RewardsManagement } from 'components/app-header/loyalty/rewards-management/';
import {
  useBankMyRewardsTierId,
  useLoyaltyCategories,
} from 'components/app-header/loyalty/rewards-management/hooks';
import { SkeletonThemeType } from 'components/layout/skeleton-theme/types';
import { useSetLoyaltyCardRewardsTierMutation } from 'generated/rbi-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { useSelectedRewardTier } from 'hooks/loyalty/use-selected-reward';
import useErrorModal from 'hooks/use-error-modal';
import { useToggleState } from 'hooks/use-toggle-state';
import { RewardTierOptionContainer } from 'pages/account/tims-rewards/reward-categories';
import { BankMyRewardsLabel } from 'pages/account/tims-rewards/reward-categories/components/bank-my-rewards-label';
import RewardCategoryDropdownHeader from 'pages/account/tims-rewards/reward-categories/components/category-dropdown-header';
import { CategoryDropdown } from 'pages/account/tims-rewards/reward-categories/components/category-dropdown/category-dropdown';
import {
  RewardTierInput,
  RewardTierNoInput,
} from 'pages/account/tims-rewards/reward-categories/components/reward-tier';
import { CategoryTitle } from 'pages/account/tims-rewards/reward-categories/components/styled';
import { RewardCategoriesModal } from 'pages/account/tims-rewards/reward-categories/reward-categories-modal';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { ICartRewardsDropdown, IRewardsDropdown, LoyaltyCategoryOption } from './types';

// TODO: remove min-height: 158px; in favor of long term fix
// min-height is a workaround for the safari nested flexbox issue
// https://github.com/philipwalton/flexbugs/issues/271
// causing wrapper to not wrap the dropdown elements properly
const CartRewardCategoryDropdownWrapper = styled.div`
  min-height: 158px;
  z-index: ${Styles.zIndex.normal};
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  align-items: center;
  flex-wrap: nowrap;
  padding: 1.3rem 0.5rem 1.7rem;
  background: ${Styles.color.grey.ten};
  border: 1px solid ${Styles.color.grey.four};

  ${Styles.desktop`
    padding: 1.3rem 1rem 1.7rem;
  `}
`;

export const RewardsDropdown: React.FC<IRewardsDropdown> = ({
  radioButtonName,
  variant,
  setSelectedRewardsTier,
  showHeader = true,
}) => {
  const enableTHRewardsTierToggleUI = useFlag(LaunchDarklyFlag.ENABLE_TH_REWARD_TIER_TOGGLE_UI);
  const { formatMessage } = useIntl();
  const { loyaltyDetails } = useLoyaltyActiveCard();
  const { user } = useAuthContext();
  const loyaltyCategories = useLoyaltyCategories();

  const loyaltyTier = (user && user.details && user.details.loyaltyTier) || '';
  const [
    dropdownOptionsVisible,
    toggleDropdownOptionsVisible,
    closeOptionsDropdown,
  ] = useToggleState();

  const [showRewardCategoriesModal, setShowRewardCategoriesModal] = useState(false);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Setting Reward Tier Failure',
  });
  const onSetRewardsTierError = useCallback(
    () => openErrorDialog({ message: formatMessage({ id: 'setRewardsTierError' }) }),
    [openErrorDialog, formatMessage]
  );

  const { bankMyRewardTierId } = useBankMyRewardsTierId();
  const { selectedRewardTierId, onChangeRewardTier } = useSelectedRewardTier(
    loyaltyCategories,
    setSelectedRewardsTier,
    { onSetRewardsTierError }
  );

  const selectedTier = useMemo(
    () =>
      selectedRewardTierId
        ? loyaltyCategories.reduce((acc: { [key: string]: LoyaltyCategoryOption }, { options }) => {
            options.forEach(val => (acc[val._id] = val));
            return acc;
          }, {})[selectedRewardTierId]
        : undefined,
    [selectedRewardTierId, loyaltyCategories]
  );

  const title = useMemo(() => {
    let dropdownTitle = (
      <CategoryTitle variant={variant}>{formatMessage({ id: 'pleaseSelect' })}</CategoryTitle>
    );

    const dropdownOptionsNotVisible = !dropdownOptionsVisible;
    const shouldShowBankMyRewardsTitle =
      dropdownOptionsNotVisible && loyaltyTier === bankMyRewardTierId;

    if (shouldShowBankMyRewardsTitle) {
      dropdownTitle = (
        <RewardTierNoInput
          name={formatMessage({ id: 'bankMyRewards' })}
          tierLabel={<BankMyRewardsLabel variant={variant} />}
          variant={variant}
        />
      );
    }

    if (dropdownOptionsNotVisible && selectedTier !== undefined) {
      dropdownTitle = (
        <RewardTierNoInput
          name={selectedTier.name?.locale}
          tierLabel={selectedTier.points}
          variant={variant}
          points={selectedTier.points}
        />
      );
    }

    return dropdownTitle;
  }, [
    bankMyRewardTierId,
    dropdownOptionsVisible,
    formatMessage,
    loyaltyTier,
    selectedTier,
    variant,
  ]);

  /**
   * NOTE: this component is in the process of being
   * deprecated in favor of the RewardsManagement Toggle.
   * This flag controls whether we should render the toggle ui.
   * If we render the toggle ui we DO NOT want to render this dropdown.
   *
   * TODO: delete this whole component once this flag is fully rolled out
   */
  if (enableTHRewardsTierToggleUI) {
    return null;
  }

  return (
    <>
      {showRewardCategoriesModal && (
        <RewardCategoriesModal
          onDismiss={() => setShowRewardCategoriesModal(false)}
          radioButtonName="see-more-options-modal-reward-tier"
          setSelectedRewardsTier={setSelectedRewardsTier}
        />
      )}
      {showHeader && (
        <RewardCategoryDropdownHeader
          variant={variant}
          points={loyaltyDetails ? loyaltyDetails.points : 0}
        />
      )}
      <CategoryDropdown
        id="rewards-dropdown"
        label={formatMessage({ id: 'selectedRewardLevelAndBankMyRewards' })}
        onClickSeeMoreActions={() => setShowRewardCategoriesModal(true)}
        optionsVisible={dropdownOptionsVisible}
        overlay
        title={title}
        toggleOptionsVisible={toggleDropdownOptionsVisible}
        variant={variant}
        handleOutsideClick={closeOptionsDropdown}
      >
        <RewardTierOptionContainer
          onClick={() => onChangeRewardTier(bankMyRewardTierId)}
          variant={variant}
        >
          <RewardTierInput
            name={formatMessage({ id: 'bankMyRewards' })}
            radioButtonName={radioButtonName}
            selected={selectedRewardTierId === bankMyRewardTierId}
            standalone={false}
            tierLabel={<BankMyRewardsLabel variant={variant} />}
            value={bankMyRewardTierId || ''}
            variant={variant}
          />
        </RewardTierOptionContainer>
        {selectedTier && (
          <RewardTierOptionContainer
            onClick={() => onChangeRewardTier(bankMyRewardTierId)}
            variant={variant}
          >
            <RewardTierInput
              name={selectedTier.name?.locale}
              radioButtonName={radioButtonName}
              selected={selectedRewardTierId === selectedTier._id}
              tierLabel={selectedTier.points}
              value={selectedTier._id}
              variant={variant}
            />
          </RewardTierOptionContainer>
        )}
        <ErrorDialog />
      </CategoryDropdown>
    </>
  );
};

export const CartRewardDropdown: React.FC<ICartRewardsDropdown> = ({
  skeletonTheme = SkeletonThemeType.PRIMARY,
}) => {
  const [setLoyaltyCardRewardsTier] = useSetLoyaltyCardRewardsTierMutation();
  return (
    <CartRewardCategoryDropdownWrapper>
      {/* NOTE: only one of these will render,
          but the components themselves handle which one */}
      <RewardsManagement skeletonTheme={skeletonTheme} data-testid="rewards-management" />
      <RewardsDropdown
        radioButtonName="cart-rewards-dropdown-reward-tier"
        setSelectedRewardsTier={setLoyaltyCardRewardsTier}
      />
    </CartRewardCategoryDropdownWrapper>
  );
};
